// src/oura/delivery/ByMonth.tsx
//
// By month: the Timeline, from real states. Every month of the term with its
// deliverables; this month open, the others folded with one fact each.

import type { Deliverable, DeliveryGroup, DeliveryPayload } from "@service/aeo/delivery";
import { dayLabel } from "@service/aeo/delivery";
import { GroupFold } from "../ui";
import type { Tone } from "../ui/tokens";
import { NextRow } from "./DeliveryList";

function monthTone(rows: Deliverable[], month: number, current: number): Tone {
    if (month > current) return "gray";
    if (rows.every((d) => d.group === "done")) return "green";
    if (rows.some((d) => d.actor?.late || d.actor?.cameBack)) return "rose";
    return "blue";
}

export function ByMonth({
    payload,
    openKey,
    onOpen,
}: {
    payload: DeliveryPayload;
    openKey: string | null;
    onOpen: (key: string, group: DeliveryGroup) => void;
}) {
    // The term's months; without a term length, the months the deliverables name.
    const last = payload.termMonths || payload.deliverables.reduce((m, d) => Math.max(m, d.month), 0);
    const months = Array.from({ length: last }, (_, i) => i + 1);
    return (
        <div>
            {months.map((m) => {
                const rows = payload.deliverables.filter((d) => d.month === m);
                if (rows.length === 0) return null;
                const done = rows.filter((d) => d.group === "done").length;
                const win = rows[0].window;
                const title = m === 1 ? "Month 1 · Foundation" : `Month ${m}`;
                // No window: the plan has not started, so there are no dates to
                // put a range between. The count is still true and still useful.
                const span = win ? `${dayLabel(win.start)} to ${dayLabel(win.end)} · ` : "";
                const note = `${span}${done} of ${rows.length} done`;
                return (
                    <GroupFold
                        key={m}
                        id={`delivery-month.${payload.businessId}.${m}`}
                        tone={monthTone(rows, m, payload.month)}
                        title={m === payload.month ? `${title} · this month` : title}
                        count={rows.length}
                        lead="square"
                        defaultFolded={m !== payload.month}
                        note={note}
                        // On a phone the dates take their own line under the month, never over it (Angel, 15 Oct).
                        wrap
                    >
                        {rows.map((d) => (
                            <NextRow key={d.key} d={d} selected={openKey === d.key} onOpen={() => onOpen(d.key, d.group)} />
                        ))}
                    </GroupFold>
                );
            })}
        </div>
    );
}
